import { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, ShoppingCart, ChefHat, Ticket, Megaphone, LogOut, Mail, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import AuthService from './services/AuthService';
import darkLogo from './darkLogo.png';
import './App.css';

const APPS = [
  {
    name: 'Manage',
    description: 'Venue management dashboard',
    url: 'https://manage.attraxion.app',
    icon: LayoutDashboard,
    accent: '#8c52ff',
    glow: 'rgba(140, 82, 255, 0.4)',
    roles: ['owner', 'admin', 'manager'],
  },
  {
    name: 'POS',
    description: 'Point of sale',
    url: 'https://pos.attraxion.app',
    icon: ShoppingCart,
    accent: '#03C4A1',
    glow: 'rgba(3, 196, 161, 0.4)',
    roles: [],
  },
  {
    name: 'KDS',
    description: 'Kitchen display system',
    url: 'https://kds.attraxion.app',
    icon: ChefHat,
    accent: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.4)',
    roles: [],
  },
  {
    name: 'Redemption',
    description: 'Arcade ticket redemption',
    url: 'https://redemption.attraxion.app',
    icon: Ticket,
    accent: '#ef4444',
    glow: 'rgba(239, 68, 68, 0.4)',
    roles: ['owner', 'admin'],
  },
  {
    name: 'Marketing',
    description: 'Marketing & promotions',
    url: 'https://marketing.attraxion.app',
    icon: Megaphone,
    accent: '#06b6d4',
    glow: 'rgba(6, 182, 212, 0.4)',
    roles: ['owner', 'admin'],
  },
];

const AttraXionLogo = () => (
  <div className="logo-text">
    ATTRA<span className="logo-x">X</span>ION
  </div>
);

function LoginView({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(true);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await AuthService.login(email, password);
      onLogin();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-section">
          {logoLoaded ? (
            <img
              src={darkLogo}
              alt="AttraXion Logo"
              className="login-logo-img"
              onError={() => setLogoLoaded(false)}
            />
          ) : (
            <AttraXionLogo />
          )}
        </div>

        <div className="login-header">
          <p className="login-subtitle">Sign in to continue to your workspace</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-alert">
              <AlertCircle size={16} strokeWidth={2} />
              <span>{error}</span>
            </div>
          )}

          <div className="login-field">
            <label className="login-label">Email</label>
            <div className={`login-input-wrap ${emailFocused ? 'focused' : ''}`}>
              <Mail size={18} strokeWidth={1.5} className="login-input-icon" />
              <input
                type="text"
                className="login-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label className="login-label">Password</label>
            <div className={`login-input-wrap ${passwordFocused ? 'focused' : ''}`}>
              <Lock size={18} strokeWidth={1.5} className="login-input-icon" />
              <input
                type="password"
                className="login-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className={`login-btn ${loading ? 'login-btn--loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <span className="login-btn-content">
                <span className="login-spinner" />
                SIGNING IN...
              </span>
            ) : (
              <span className="login-btn-content">
                SIGN IN
                <ArrowRight size={18} strokeWidth={2} />
              </span>
            )}
          </button>
        </form>

      </div>
    </div>
  );
}

function AppCard({ app, index }) {
  const Icon = app.icon;

  return (
    <a
      href={app.url}
      className="app-card"
      style={{
        '--card-accent': app.accent,
        '--card-glow': app.glow,
        animationDelay: `${index * 80}ms`,
      }}
      rel="noopener noreferrer"
    >
      <div className="app-card__icon-ring">
        <Icon size={28} strokeWidth={1.5} />
      </div>
      <div className="app-card__body">
        <h3 className="app-card__name">{app.name}</h3>
        <p className="app-card__desc">{app.description}</p>
      </div>
      <div className="app-card__arrow">&#8599;</div>
    </a>
  );
}

function LaunchpadView({ onLogout }) {
  const staffName = AuthService.getStaffName();
  const venueName = AuthService.getVenueName();
  const userRole = AuthService.getUserRole();
  const visibleApps = APPS.filter(app =>
    app.roles.length === 0 || app.roles.includes(userRole)
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  return (
    <div className={`launchpad-scene ${mounted ? 'launchpad-scene--visible' : ''}`}>
      <div className="launchpad-atmosphere">
        <div className="atmo-orb atmo-orb--1" />
        <div className="atmo-orb atmo-orb--2" />
        <div className="atmo-orb atmo-orb--3" />
      </div>

      <header className="lp-header">
        <div className="lp-header__brand">
          <img
            src={darkLogo}
            alt="AttraXion"
            className="lp-header__logo"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div style={{ display: 'none' }}><AttraXionLogo /></div>
        </div>
        <div className="lp-header__meta">
          <div className="lp-header__info">
            {venueName && <span className="lp-header__venue">{venueName}</span>}
            {staffName && <span className="lp-header__staff">{staffName}</span>}
          </div>
          <button className="lp-header__logout" onClick={onLogout}>
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      <main className="lp-main">
        <div className="lp-hero">
          <h1 className="lp-hero__title">Your Apps</h1>
          <p className="lp-hero__sub">Select an application to launch</p>
        </div>

        <div className="app-grid">
          {visibleApps.map((app, i) => (
            <AppCard key={app.name} app={app} index={i} />
          ))}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(AuthService.isAuthenticated());

  const handleLogout = useCallback(() => {
    AuthService.logout();
    setAuthed(false);
  }, []);

  if (!authed) {
    return <LoginView onLogin={() => setAuthed(true)} />;
  }

  return <LaunchpadView onLogout={handleLogout} />;
}
